import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * News stands — canonical member-facing routes (`/public/news-stands*`,
 * `publicTenantMiddleware`, `requireModule('newsstand')`).
 *
 * Data model (matches the tenant-API):
 *  - a **news stand** is a publication ("newsletter") — `newsstand_id`,
 *    `name`, `description`, a computed cover (`cover_url`), and an
 *    `article_count`.
 *  - an **article** is an **issue** of that publication — a downloadable
 *    file (PDF) addressed by `file_storage_path` / `attachments`, with a
 *    cover image and an availability window. There is no inline article
 *    body; issues are downloaded via the `/download` endpoint, which
 *    returns a short-lived signed URL.
 */

export interface NewsStandSummary {
  newsstand_id: string;
  name: string;
  description?: string;
  /** Signed/public cover image URL (computed server-side). */
  cover_url?: string;
  /** Number of published issues in this stand. */
  article_count?: number;
}

export interface NewsIssue {
  article_id: string;
  newsstand_id: string;
  /** Issue title. */
  name: string;
  description?: string;
  /** Signed/public cover image URL (computed server-side). */
  cover_url?: string;
  /** Best-effort download file name. */
  file_name?: string;
  mime_type?: string;
  is_available: boolean;
  /** ISO date this issue went (or goes) live — `available_after` or `created_at`. */
  published_at: string;
}

interface RawNewsStand {
  newsstand_id: string;
  name?: string;
  description?: string | null;
  cover_url?: string | null;
  default_cover_storage_path?: string | null;
  article_count?: number | null;
}

interface RawAttachment {
  name?: string;
  file_name?: string;
  mime?: string;
  mime_type?: string;
}

interface RawNewsArticle {
  article_id: string;
  newsstand_id: string;
  name?: string;
  description?: string | null;
  cover_url?: string | null;
  file_storage_path?: string | null;
  attachments?: RawAttachment[] | null;
  is_available?: boolean;
  available_after?: string | null;
  created_at?: string | null;
}

function toIsoDate(value: unknown): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function basename(path?: string | null): string | undefined {
  if (!path) return undefined;
  const segment = path.split(/[\\/]/).pop();
  return segment && segment.trim() ? segment.trim() : undefined;
}

function mapStand(raw: RawNewsStand): NewsStandSummary {
  return {
    newsstand_id: raw.newsstand_id,
    name: (raw.name ?? 'News').trim() || 'News',
    description: raw.description ?? undefined,
    cover_url: raw.cover_url ?? undefined,
    article_count: raw.article_count ?? undefined,
  };
}

function mapIssue(raw: RawNewsArticle): NewsIssue {
  const attachment = raw.attachments?.[0];
  const file_name =
    attachment?.name ??
    attachment?.file_name ??
    basename(raw.file_storage_path) ??
    `${(raw.name ?? 'issue').trim() || 'issue'}.pdf`;
  return {
    article_id: raw.article_id,
    newsstand_id: raw.newsstand_id,
    name: (raw.name ?? 'Untitled issue').trim() || 'Untitled issue',
    description: raw.description ?? undefined,
    cover_url: raw.cover_url ?? undefined,
    file_name,
    mime_type: attachment?.mime ?? attachment?.mime_type ?? undefined,
    is_available: raw.is_available ?? true,
    published_at: toIsoDate(raw.available_after ?? raw.created_at),
  };
}

export async function fetchNewsStands(): Promise<NewsStandSummary[]> {
  const { data } = await tenantApi.get('/public/news-stands', { params: { page: 1, limit: 100 } });
  return unwrapList<RawNewsStand>(data).map(mapStand);
}

/**
 * Latest published issues across ALL news stands (for the home News widget).
 * Aggregates each stand's available articles, sorts by published date desc,
 * and returns the top `limit`.
 */
export async function fetchLatestNewsIssues(limit = 3): Promise<NewsIssue[]> {
  const stands = await fetchNewsStands();
  if (stands.length === 0) return [];
  const perStand = await Promise.all(
    stands.map((s) =>
      tenantApi
        .get(`/public/news-stands/${s.newsstand_id}/articles`, {
          params: { page: 1, limit: 20, is_available: true },
        })
        .then((res) => unwrapList<RawNewsArticle>(res.data).map(mapIssue))
        .catch(() => [] as NewsIssue[]),
    ),
  );
  return perStand
    .flat()
    .filter((issue) => issue.is_available)
    .sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''))
    .slice(0, limit);
}

export async function fetchNewsStand(
  newsstandId: string,
): Promise<{ stand: NewsStandSummary; issues: NewsIssue[] }> {
  const [standsRes, articlesRes] = await Promise.all([
    tenantApi.get('/public/news-stands', { params: { page: 1, limit: 100 } }),
    tenantApi.get(`/public/news-stands/${newsstandId}/articles`, {
      params: { page: 1, limit: 100, is_available: true },
    }),
  ]);
  const stands = unwrapList<RawNewsStand>(standsRes.data).map(mapStand);
  const stand =
    stands.find((s) => s.newsstand_id === newsstandId) ??
    ({ newsstand_id: newsstandId, name: 'News' } as NewsStandSummary);
  const issues = unwrapList<RawNewsArticle>(articlesRes.data).map(mapIssue);
  return { stand, issues };
}

/**
 * Resolve a short-lived signed download URL for an issue. The endpoint
 * returns `{ download_url }` (it does not stream bytes), so callers fetch
 * that URL directly to obtain the file.
 */
export async function fetchIssueDownloadUrl(
  newsstandId: string,
  articleId: string,
): Promise<string> {
  const { data } = await tenantApi.get(
    `/public/news-stands/${newsstandId}/articles/${articleId}/download`,
  );
  const payload = unwrap<{ download_url?: string }>(data);
  if (!payload?.download_url) throw new Error('No download available for this issue.');
  return payload.download_url;
}
